/**
 * Lab member Account Statement — settled P&L rows (not the ledger).
 *
 * Agent-API `POST /v1/account/getDownlineAccountsForMember` (+ New) +
 * `getDownlineTotalNetAmount`. Server picks today→PG / else→BQ like Lab PL.
 * Eye → Game Detail; CM Graph → archive modal.
 */
import { useMemo, useState } from 'react'
import {
  Banner,
  Button,
  Card,
  DataTable,
  DEFAULT_PAGE_SIZE,
  Field,
  IconButton,
  Input,
  Legend,
  Pager,
  Segmented,
  Select,
  StateBlock,
  type Column,
} from '@w7/ui'
import {
  useMemberStatement,
  useMemberTotalNetAmount,
} from '@/api/hooks/member/useMemberStatement'
import type { PlStatementRowView } from '@/api/map/plStatement'
import { FIXTURE_GAMES } from '@/api/map/game'
import { useGames, useSession, useSubGames } from '@/app/queries'
import { Money } from '@/app/bits'
import { EMPTY, lll } from '@/lib/format'
import type { Account } from '@/data/types'
import { PlStatementDetailDrawer } from '@/features/reports/PlStatementDetailDrawer'
import { PlStatementGraphModal } from '@/features/reports/PlStatementGraphModal'
import {
  canShowMemberStatementGraph,
  isoDatetimeOffset,
  memberStatementEventLabel,
  memberStatementGraphFilter,
  memberStatementNetAmt,
  memberStatementStatus,
  memberStatementTotalNetShare,
  memberStatementTotalNetShareFromApi,
  isStatementTransferSubGame,
  type MemberStatementMode,
} from '@/features/member/memberStatement'

const SPORTSBOOK_ID = FIXTURE_GAMES.find((g) => g.name === 'SportsBook')?._id ?? '5b36053e46123555cca93993'

export function MemberStatementTab({ account }: { account: Account }) {
  const { data: session } = useSession()
  const isCompanyMaster = session?.account.role === 'company-master'

  const [gameId, setGameId] = useState(SPORTSBOOK_ID)
  const [subGame, setSubGame] = useState('All')
  const [fromDate, setFromDate] = useState(() => isoDatetimeOffset(0))
  const [toDate, setToDate] = useState(() => isoDatetimeOffset(0, true))
  const [mode, setMode] = useState<MemberStatementMode>('player-pnl')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<PlStatementRowView | null>(null)
  const [graphRow, setGraphRow] = useState<PlStatementRowView | null>(null)
  const [applied, setApplied] = useState({
    gameId: SPORTSBOOK_ID,
    subGame: 'All',
    fromDate: isoDatetimeOffset(0),
    toDate: isoDatetimeOffset(0, true),
  })

  const games = useGames()
  const subGames = useSubGames(gameId === 'all' ? null : gameId)
  const pageSize = DEFAULT_PAGE_SIZE

  const query = useMemo(
    () => ({
      memberId: account.id,
      range: { from: applied.fromDate, to: applied.toDate },
      page,
      pageSize,
      ...(applied.gameId && applied.gameId !== 'all' ? { game: applied.gameId } : {}),
      ...(applied.subGame && applied.subGame !== 'All' ? { subGame: applied.subGame } : {}),
    }),
    [account.id, applied, page, pageSize],
  )

  const statement = useMemberStatement(query)
  const totalNet = useMemberTotalNetAmount(query)
  const rows = statement.data?.rows ?? []
  const total = statement.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const pageMemberPnlSum = rows.reduce((sum, row) => sum + row.memberPnl, 0)
  const fallbackShare = memberStatementTotalNetShare(statement.data?.netPnl, pageMemberPnlSum)
  const totalNetShare = memberStatementTotalNetShareFromApi(
    totalNet.data,
    mode,
    account.role,
    fallbackShare,
  )
  const eventHeader = mode === 'player-pnl' ? 'Player P&L' : 'P&L'
  const ccy = account.currencyType

  const gameOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...(games.data ?? []).map((g) => ({ value: g._id, label: g.name })),
    ],
    [games.data],
  )
  const subOptions = useMemo(
    () => [
      { value: 'All', label: 'All' },
      ...(subGames.data ?? []).map((name) => ({ value: name, label: name })),
    ],
    [subGames.data],
  )

  const graphFilter = graphRow
    ? memberStatementGraphFilter(graphRow, account.username)
    : null

  const columns = useMemo<Column<PlStatementRowView>[]>(() => {
    const cols: Column<PlStatementRowView>[] = [
      {
        id: 'when',
        header: 'Date',
        width: 168,
        cell: (row) => lll(row.settledAt),
        sortValue: (row) => row.settledAt,
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => row.game,
        sortValue: (row) => row.game,
      },
      {
        id: 'event',
        header: eventHeader,
        cell: (row) => (
          <button
            type="button"
            className="w7-link"
            onClick={() => setSelected(row)}
            title={memberStatementEventLabel(row)}
          >
            {memberStatementEventLabel(row)}
          </button>
        ),
        sortValue: (row) => memberStatementEventLabel(row),
      },
      {
        id: 'bets',
        header: 'Bet Count',
        numeric: true,
        cell: (row) => row.betCount,
        sortValue: (row) => row.betCount,
      },
      {
        id: 'net',
        header: 'Net Amt.',
        numeric: true,
        cell: (row) => (
          <Money
            value={memberStatementNetAmt(row, mode, account.role)}
            currency={row.currencyType ?? ccy}
            signed
            alreadyDisplay
          />
        ),
        sortValue: (row) => memberStatementNetAmt(row, mode, account.role),
      },
      {
        id: 'prevBal',
        header: 'Previous balance',
        numeric: true,
        cell: (row) =>
          row.previousBalance != null ? (
            <Money
              value={row.previousBalance}
              currency={row.currencyType ?? ccy}
              alreadyDisplay
            />
          ) : (
            EMPTY
          ),
        sortValue: (row) => row.previousBalance ?? Number.NEGATIVE_INFINITY,
      },
      {
        id: 'nextBal',
        header: 'Next balance',
        numeric: true,
        cell: (row) =>
          row.nextBalance != null ? (
            <Money value={row.nextBalance} currency={row.currencyType ?? ccy} alreadyDisplay />
          ) : (
            EMPTY
          ),
        sortValue: (row) => row.nextBalance ?? Number.NEGATIVE_INFINITY,
      },
      {
        id: 'expo',
        header: 'Exposure',
        numeric: true,
        cell: (row) =>
          row.exposure != null ? (
            <Money value={row.exposure} currency={row.currencyType ?? ccy} alreadyDisplay />
          ) : (
            EMPTY
          ),
        sortValue: (row) => row.exposure ?? Number.NEGATIVE_INFINITY,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => {
          const status = memberStatementStatus(row)
          if (status === 'Rollback' || status === 'P&L Rollback') {
            return <span style={{ color: 'var(--w7-warn, orange)' }}>{status}</span>
          }
          if (status === 'Invalid' || status === 'Invalidate P&L') {
            return <span className="w7-loss">{status}</span>
          }
          return status
        },
        sortValue: (row) => memberStatementStatus(row),
      },
      {
        id: 'detail',
        header: 'Details',
        width: 72,
        cell: (row) =>
          isStatementTransferSubGame(row.sport) ? (
            EMPTY
          ) : (
            <IconButton
              name="eye"
              size="sm"
              label="Game Detail"
              onClick={() => setSelected(row)}
            />
          ),
      },
    ]
    if (isCompanyMaster) {
      cols.push({
        id: 'graph',
        header: 'Graph',
        width: 72,
        cell: (row) =>
          canShowMemberStatementGraph(row) ? (
            <IconButton
              name="pnl"
              size="sm"
              label="View Graph"
              onClick={() => setGraphRow(row)}
            />
          ) : (
            EMPTY
          ),
      })
    }
    return cols
  }, [account.role, ccy, eventHeader, isCompanyMaster, mode])

  const runSearch = () => {
    setPage(1)
    setApplied({ gameId, subGame, fromDate, toDate })
  }

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Account Statement filters">
        <div className="w7-transfer-filter-row">
          <Field label="Select Games">
            {({ id }) => (
              <Select
                id={id}
                aria-label="Select Games"
                value={gameId}
                onChange={(e) => {
                  setGameId(e.target.value)
                  setSubGame('All')
                }}
                options={gameOptions}
              />
            )}
          </Field>
          <Field label="Select Sub Games">
            {({ id }) => (
              <Select
                id={id}
                aria-label="Select Sub Games"
                value={subGame}
                onChange={(e) => setSubGame(e.target.value)}
                options={subOptions}
              />
            )}
          </Field>
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="datetime-local"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input
                id={id}
                type="datetime-local"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            )}
          </Field>
          <Button type="button" variant="primary" busy={statement.isFetching} onClick={runSearch}>
            Search
          </Button>
        </div>
      </div>

      <div className="w7-transfer-filter-row" style={{ alignItems: 'center' }}>
        <Segmented
          label="Statement type"
          value={mode}
          onChange={(v) => setMode(v as MemberStatementMode)}
          items={[
            { id: 'player-pnl', label: 'Player P&L Statement' },
            { id: 'pnl', label: 'P&L Statement' },
          ]}
        />
        <div className="w7-transfer-net-share">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--w7-s3)' }}>
            <span className="w7-muted" style={{ fontSize: 'var(--w7-fs-sm)' }}>
              Total Net Share
            </span>
            <Money value={totalNetShare} currency={ccy} signed alreadyDisplay />
          </div>
          <Legend
            items={[
              { label: 'Player Loss', color: 'var(--w7-loss)' },
              { label: 'Player Win', color: 'var(--w7-profit)' },
            ]}
          />
        </div>
      </div>

      {statement.data?.unavailable ? (
        <Banner tone="warn" title="Statement source unavailable">
          {statement.data.unavailable.reason}
        </Banner>
      ) : null}

      {statement.isError ? (
        <Banner tone="error" title="Statement could not be loaded">
          {(statement.error as Error)?.message}
        </Banner>
      ) : null}

      <Card
        flush
        title="Account Statement"
        subtitle={`${account.username} · ${applied.fromDate.slice(0, 16).replace('T', ' ')} → ${applied.toDate.slice(0, 16).replace('T', ' ')}`}
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={pageSize}
          />
        }
      >
        <DataTable
          label={`Account statement for ${account.username}`}
          rowKey={(row) => row.id}
          loading={statement.isLoading}
          skeletonRows={8}
          rows={rows}
          columns={columns}
          empty={
            <StateBlock icon="pnl" title="No data found">
              No account statement rows for {account.username} between{' '}
              {applied.fromDate.slice(0, 16).replace('T', ' ')} and{' '}
              {applied.toDate.slice(0, 16).replace('T', ' ')}.
            </StateBlock>
          }
        />
      </Card>

      {selected ? (
        <PlStatementDetailDrawer
          row={selected}
          currency={selected.currencyType ?? ccy}
          onClose={() => setSelected(null)}
        />
      ) : null}

      <PlStatementGraphModal
        open={Boolean(graphRow)}
        filter={graphFilter}
        eventName={graphRow?.eventName ?? ''}
        onClose={() => setGraphRow(null)}
      />
    </div>
  )
}
